import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ClientLogin } from './ClientLogin';
import { ClientDashboard } from './ClientDashboard';
import { SetPassword } from './SetPassword';
import { ForgotPassword } from './ForgotPassword';
import { ResetPassword } from './ResetPassword';
import { OrdersTab } from './routes/OrdersTab';
import { AddressesTab } from './routes/AddressesTab';
import { SubscriptionTab } from './routes/SubscriptionTab';
import { GiftIdeasTab } from './routes/GiftIdeasTab';

interface ClientData {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  email_verified: boolean;
  last_login?: string;
  created_at: string;
}

export function ClientApp() {
  const [client, setClient] = useState<ClientData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only check session once on app load
    const initialPath = window.location.pathname;
    const isLoginPage = initialPath.includes('/login') || initialPath.includes('/forgot-password') || 
                       initialPath.includes('/set-password') || initialPath.includes('/reset-password');
    
    if (!isLoginPage && !client) {
      checkExistingSession();
    } else {
      setIsLoading(false);
    }
  }, []); // No dependencies - only run once on mount

  // Separate effect to handle navigation based on auth state
  useEffect(() => {
    if (!isLoading && !client) {
      const path = location.pathname;
      const isLoginPage = path.includes('/login') || path.includes('/forgot-password') || 
                         path.includes('/set-password') || path.includes('/reset-password');
      
      if (!isLoginPage) {
        navigate('/login');
      }
    }
  }, [isLoading, client, location.pathname, navigate]);

  const checkExistingSession = async () => {
    try {
      const response = await fetch('/api/customer/profile');
      const result = await response.json();

      if (result.success) {
        setClient(result.data.customer);
      }
    } catch (error) {
      console.error('Session check error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = (clientData: ClientData) => {
    setClient(clientData);
    navigate('/customer/dashboard/orders');
  };

  const handleLogout = () => {
    setClient(null);
    navigate('/login');
  };

  const handleForgotPassword = () => {
    navigate('/customer/forgot-password');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={
        client ? (
          <ClientDashboard initialClient={client} onLogout={handleLogout} />
        ) : (
          <ClientLogin onLogin={handleLogin} onForgotPassword={handleForgotPassword} />
        )
      } />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={
        client ? (
          <ClientDashboard initialClient={client} onLogout={handleLogout} />
        ) : (
          <ClientLogin onLogin={handleLogin} onForgotPassword={handleForgotPassword} />
        )
      }>
        <Route path="orders" element={<OrdersTab />} />
        <Route path="addresses" element={<AddressesTab />} />
        <Route path="subscriptions" element={<SubscriptionTab />} />
        <Route path="gift-ideas" element={<GiftIdeasTab />} />
      </Route>
      <Route path="*" element={
        client ? (
          <ClientDashboard initialClient={client} onLogout={handleLogout} />
        ) : (
          <ClientLogin onLogin={handleLogin} onForgotPassword={handleForgotPassword} />
        )
      } />
    </Routes>
  );
}